/**
 * Claim record schema — DataTable columns + FormModal fields for the
 * Claims half of Step 7 (`cybersecurity_goals_agent`, per ISO 21434
 * RQ-09-06). Claims are generated for "Share" / "Retain" treatment
 * decisions — terminal outputs, not consumed by downstream steps.
 *
 * Real DB shape:
 *   { claim_id (PK), claim_statement, justification, risk_ids: string[],
 *     // hierarchical: source, source_session_id, is_relevant,
 *     // relevance_justification }
 */

export interface ClaimRecord {
  claim_id: string;
  claim_statement?: string;
  justification?: string;
  risk_ids?: string[];
  source?: string;
  source_session_id?: string;
  is_relevant?: boolean;
  relevance_justification?: string;
  [key: string]: unknown;
}

const joinList = (v: unknown): string =>
  Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v);

export const CLAIM_COLUMNS_READ = [
  { key: 'claim_id', label: 'ID', sortable: true, width: '8%' },
  { key: 'claim_statement', label: 'Claim Statement', sortable: false, width: '38%' },
  { key: 'justification', label: 'Justification', sortable: false, width: '38%' },
  {
    key: 'risk_ids',
    label: 'Linked Risks',
    sortable: false,
    width: '16%',
    render: (row: ClaimRecord) => joinList(row.risk_ids),
  },
];

export const CLAIM_COLUMNS_EDIT = [
  { key: 'claim_id', label: 'ID', sortable: true, width: '7%' },
  { key: 'claim_statement', label: 'Claim Statement', sortable: false, width: '37%' },
  { key: 'justification', label: 'Justification', sortable: false, width: '37%' },
  {
    key: 'risk_ids',
    label: 'Linked Risks',
    sortable: false,
    width: '15%',
    render: (row: ClaimRecord) => joinList(row.risk_ids),
  },
];

export const CLAIM_ACTIONS_COLUMN_WIDTH = '4%';

const CLAIM_ID_PATTERN = /^[A-Z0-9_-]+$/;

export const CLAIM_FORM_FIELDS = [
  {
    name: 'claim_id',
    label: 'Claim ID',
    type: 'text',
    required: true,
    autoFocus: true,
    placeholder: 'e.g. CLAIM-01',
    helpText: 'Unique identifier (per ISO 21434 RQ-09-06).',
    validate: (v: unknown) => {
      const s = String(v ?? '').trim();
      if (!s) return 'Claim ID is required';
      if (!CLAIM_ID_PATTERN.test(s)) {
        return 'Claim ID must contain only uppercase letters, numbers, underscores, and hyphens';
      }
      return null;
    },
  },
  {
    name: 'risk_ids',
    label: 'Linked Risks',
    type: 'multi-select',
    required: true,
    options: [] as Array<{ value: string; label: string }>,
    helpText: 'Risks this claim addresses (N:M relationship).',
    defaultValue: [],
  },
  {
    name: 'claim_statement',
    label: 'Claim Statement',
    type: 'textarea',
    required: true,
    rows: 3,
    placeholder: 'Statement about the risk being shared or retained.',
    validate: (v: unknown) => {
      const s = String(v ?? '').trim();
      if (s.length < 20) return 'Claim statement must be at least 20 characters.';
      return null;
    },
  },
  {
    name: 'justification',
    label: 'Justification',
    type: 'textarea',
    required: true,
    rows: 3,
    placeholder: 'Explain why sharing or retaining the risk is acceptable.',
    validate: (v: unknown) => {
      const s = String(v ?? '').trim();
      if (s.length < 20) return 'Justification must be at least 20 characters.';
      return null;
    },
  },
];
